import _cloneDeep from "lodash.clonedeep";
import ImageFlowObject from "../../model/ImageFlowObject";
import guidGenerator, {
  resizeLogic,
  dataURLtoFile,
} from "../utils/commonFunctions";
import { Send } from "../fsm/useMachine";
import { ADD_MMO, ADD_BACKGROUND_MMO, FlowObjects } from "../fsm/types";
import VideoFlowObject from "../../model/VideoFlowObject";
import ImageAsset from "../../model/Assets";
import PdfFlowObject from "../../model/PdfFlowObject";
//@ts-ignore
import PDFJS from "@bundled-es-modules/pdfjs-dist/build/pdf";
import GalleryAsset from "../../model/GalleryAsset";
import GalleryFlowObject from "../../model/GalleryFlowObject";

// Use unpkg.com CDN which has better CORS support
// Match the actual installed version of @bundled-es-modules/pdfjs-dist (2.16.106)
PDFJS.GlobalWorkerOptions.workerSrc =
  "https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js";

/**
 * Converts a `File` into a {@link ImageFlowObject}
 * @param image `File`
 * @param viewSize {@link Flow.viewSize}
 * @param send FSM send
 */
export const addImageHelper = (
  image: File,
  viewSize: { width: number; height: number },
  send: Send,
  screenIndex: number
) => {
  let imageObject = ImageFlowObject.default(
    { __tag: "file", value: image },
    image.name
  );
  let draftObject = _cloneDeep(imageObject);
  let imgreader = new FileReader();
  // '!' use: we've just created this object above...guaranteed to have an externalURL
  if (imageObject.originalImageAsset!.externalURL != undefined) {
    if (imageObject.originalImageAsset!.externalURL.__tag === "file")
      imgreader.readAsDataURL(
        imageObject.originalImageAsset!.externalURL.value
      );
    imgreader.onloadend = async () => {
      if (imgreader.readyState == 2) {
        imageObject.originalImageAsset!.__name = image.name;
        imageObject.croppedImageAsset!.__name = image.name;
        if (typeof imgreader.result == "string") {
          imageObject.originalImageAsset!.externalURL = {
            __tag: "string",
            value: imgreader.result,
          };
          imageObject.croppedImageAsset = _cloneDeep(
            imageObject.originalImageAsset
          );
        }
        imageObject = await resizeLogic(imageObject, viewSize);

        draftObject.frame = imageObject.frame;
        send({ type: ADD_MMO, mmo: imageObject, draft: draftObject, screenIndex });
      }
    };
  }
};

/**
 * Converts a `File` into a {@link VideoFlowObject}
 * @param image `File`
 * @param viewSize {@link Flow.viewSize}
 * @param send FSM send
 */
export const addVideoHelper = (
  video_: File,
  viewSize: { width: number; height: number },
  send: Send,
  screenIndex: number
) => {
  let videoObject = VideoFlowObject.default({
    vidSrc: { __tag: "file", value: video_ },
    name: video_.name,
    isYoutube: "NO",
  });
  let draftObject = _cloneDeep(videoObject);

  // Use blob URL instead of reading entire file as base64 - much faster and more reliable
  const blobUrl = URL.createObjectURL(video_);
  const video = document.createElement("video");
  const canvas = document.createElement("canvas");
  video.src = blobUrl;
  video.muted = true; // Required for autoplay policies
  video.preload = "metadata";
  const ctx = canvas.getContext("2d");

  const cleanup = () => {
    URL.revokeObjectURL(blobUrl);
  };

  video.addEventListener(
    "loadeddata",
    () => {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      if (!isNaN(video.duration)) {
        video.currentTime = Math.min(1, video.duration / 2);
      }
    },
    false
  );

  video.addEventListener(
    "seeked",
    async () => {
      ctx?.drawImage(video, 0, 0);
      let img = canvas.toDataURL("image/jpeg");
      let name =
        video_.name == undefined ? `${videoObject.id}.png` : video_.name;
      let { src, draft } = addImageAssets(
        img,
        name,
        videoObject,
        draftObject
      );
      src = (await resizeLogic(src, viewSize)) as VideoFlowObject;
      src.__forceUseImage = true;
      draft.frame = src.frame;
      cleanup();
      send({ type: ADD_MMO, mmo: src, draft: draft, screenIndex });
    },
    false
  );

  video.addEventListener(
    "error",
    (e) => {
      console.error("[addVideoHelper] Video load error:", e);
      cleanup();
      // Still add the video object even if thumbnail fails - use placeholder
      videoObject.frame = { x: 100, y: 100, width: 400, height: 300 };
      draftObject.frame = videoObject.frame;
      send({ type: ADD_MMO, mmo: videoObject, draft: draftObject, screenIndex });
    },
    false
  );
};

/**
 * Converts a `File` into a {@link PdfFlowObject}
 * @param image `File`
 * @param viewSize {@link Flow.viewSize}
 * @param send FSM send
 */
export const addPdfHelper = (
  pdf: File,
  viewSize: { width: number; height: number },
  send: Send,
  screenIndex: number
) => {
  let pdfObject = PdfFlowObject.default(
    { __tag: "file", value: pdf },
    pdf.name
  );
  let draftObject = _cloneDeep(pdfObject);
  const reader = new FileReader();
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  reader.onload = async function () {
    //Step 4:turn array buffer into typed array
    //@ts-ignore
    const typedarray = new Uint8Array(reader.result);
    let loadingTask = PDFJS.getDocument(typedarray);
    loadingTask.promise
      .then(async (pdf: any) => {
        pdf.getPage(1).then(async (page: any) => {
          const scale = 1.5;
          const viewport = page.getViewport({ scale });
          canvas.height = viewport.height;
          canvas.width = viewport.width;
          const task = page.render({
            canvasContext: ctx,
            viewport: viewport,
          });
          task.promise.then(async function () {
            let img = canvas.toDataURL("image/jpeg");
            let name = pdf.name == undefined ? `${pdfObject.id}.png` : pdf.name;
            let { src, draft } = addImageAssets(
              img,
              name,
              pdfObject,
              draftObject
            );
            src.__forceUseImage = true;
            src.frame.width = canvas.width;
            src.frame.height = canvas.height;
            src = await resizeLogic(src, viewSize);
            draft.frame = src.frame;
            send({ type: ADD_MMO, mmo: src, draft: draft, screenIndex });
          });
          return task.promise;
        });
      })
      .catch((e: any) => console.log(e));
  };
  if (pdfObject.pdfAsset!.externalURL.__tag === "file")
    //Step 3:Read the file as ArrayBuffer, pdfObject created above. GUARANTEED to have a pdfAsset
    reader.readAsArrayBuffer(pdfObject.pdfAsset!.externalURL.value);
};

/**
 * Converts a `File` into a {@link GalleryFlowObject}
 * @param image `File`
 * @param viewSize {@link Flow.viewSize}
 * @param send FSM send
 */
export const addGalleryHelper = async (
  gallery: GalleryAsset,
  viewSize: { width: number; height: number },
  send: Send,
  screenIndex: number
) => {
  let galleryObject = GalleryFlowObject.default(
    gallery.fullSizedImages[0].externalURL,
    `${guidGenerator()}.png`,
    gallery
  );
  let draftObject = _cloneDeep(galleryObject);
  galleryObject = await new Promise<GalleryFlowObject>((resolve, reject) => {
    let previewAsset = gallery.fullSizedImages[0];
    let previewReader = new FileReader();
    if (previewAsset.externalURL.__tag === "file")
      previewReader.readAsDataURL(previewAsset.externalURL.value);
    previewReader.onloadend = async () => {
      if (previewReader.readyState == 2) {
        if (typeof previewReader.result == "string") {
          // Galleryobject created above. GUARANTEED to have originalImageAsset
          galleryObject.originalImageAsset!.externalURL = {
            __tag: "string",
            value: previewReader.result,
          };
          galleryObject.croppedImageAsset = _cloneDeep(
            galleryObject.originalImageAsset
          );
          galleryObject.galleryAssets!.fullSizedImages[0].externalURL = {
            __tag: "string",
            value: previewReader.result,
          };
        }
        galleryObject = await resizeLogic(galleryObject, viewSize);

        draftObject.frame = galleryObject.frame;
        resolve(galleryObject);
      }
    };
  });
  if (gallery.fullSizedImages.length === 1) {
    draftObject.frame = galleryObject.frame;
    send({ type: ADD_MMO, mmo: galleryObject, draft: draftObject, screenIndex });
  }
  for (let i = 1; i < gallery.fullSizedImages.length; i++) {
    let img: ImageAsset = gallery.fullSizedImages[i];
    if (
      img.externalURL.__tag === "string" &&
      img.externalURL.value.startsWith("http")
    )
      continue;
    let galleryReader = new FileReader();
    if (img.externalURL.__tag === "file")
      galleryReader.readAsDataURL(img.externalURL.value);
    galleryReader.onloadend = async () => {
      if (galleryReader.readyState == 2) {
        if (typeof galleryReader.result == "string") {
          gallery.fullSizedImages[i].externalURL = {
            __tag: "string",
            value: galleryReader.result,
          };
          galleryObject.galleryAssets!.thumbnailImages[i].externalURL = {
            __tag: "string",
            value: galleryReader.result,
          };
          galleryObject.galleryAssets!.lowQualityImages[i].externalURL = {
            __tag: "string",
            value: galleryReader.result,
          };
          galleryObject = await resizeLogic(galleryObject, viewSize);
          if (i === galleryObject.galleryAssets!.fullSizedImages.length - 1) {
            draftObject.frame = galleryObject.frame;
            send({ type: ADD_MMO, mmo: galleryObject, draft: draftObject, screenIndex });
          }
        }
      }
    };
  }
};

/**
 * Creates a background image MMO sized to fill the screen, inserted at index 0 (back of z-order).
 * Skips resizeLogic since the image should cover the full viewport.
 */
export const addBackgroundImageHelper = (
  image: File,
  viewSize: { width: number; height: number },
  send: Send,
  screenIndex: number
) => {
  let imageObject = ImageFlowObject.default(
    { __tag: "file", value: image },
    image.name
  );
  let draftObject = _cloneDeep(imageObject);
  let imgreader = new FileReader();
  if (imageObject.originalImageAsset!.externalURL != undefined) {
    if (imageObject.originalImageAsset!.externalURL.__tag === "file")
      imgreader.readAsDataURL(
        imageObject.originalImageAsset!.externalURL.value
      );
    imgreader.onloadend = async () => {
      if (imgreader.readyState == 2) {
        imageObject.originalImageAsset!.__name = image.name;
        imageObject.croppedImageAsset!.__name = image.name;
        if (typeof imgreader.result == "string") {
          imageObject.originalImageAsset!.externalURL = {
            __tag: "string",
            value: imgreader.result,
          };
          imageObject.croppedImageAsset = _cloneDeep(
            imageObject.originalImageAsset
          );
        }
        // Size to fill the full viewport instead of using resizeLogic
        imageObject.frame = {
          x: 0,
          y: 0,
          width: viewSize.width,
          height: viewSize.height,
        };
        draftObject.frame = imageObject.frame;
        send({ type: ADD_BACKGROUND_MMO, mmo: imageObject, draft: draftObject, screenIndex });
      }
    };
  }
};

/**
 * Routes a dropped/pasted file to the appropriate helper based on MIME type.
 * Returns true if the file was handled, false otherwise.
 */
export const addFileByType = (
  file: File,
  viewSize: { width: number; height: number },
  send: Send,
  screenIndex: number
): boolean => {
  if (file.type.startsWith("image/")) {
    addImageHelper(file, viewSize, send, screenIndex);
    return true;
  } else if (file.type === "video/mp4") {
    addVideoHelper(file, viewSize, send, screenIndex);
    return true;
  } else if (file.type === "application/pdf") {
    addPdfHelper(file, viewSize, send, screenIndex);
    return true;
  }
  return false;
};

/**
 * Abstraction over deep copying an image to both {@link FlowObject.originalImageAsset} and {@link FlowObject.croppedImageAsset} as a file in `draft` and as base64 `src`
 * @param dataurl base64 string
 * @param name name of file
 * @param src a {@link FlowObjects}
 * @param draft {@link FlowObjects} intended to be saved for drafting
 */
function addImageAssets(
  dataurl: string,
  name: string,
  src: FlowObjects,
  draft: FlowObjects
): { src: FlowObjects; draft: FlowObjects } {
  let asset = ImageAsset.default({ __tag: "string", value: dataurl });
  let fileAsset = ImageAsset.default({
    __tag: "file",
    value: dataURLtoFile(dataurl, name),
  });
  src.originalImageAsset = asset;
  src.croppedImageAsset = _cloneDeep(asset);
  draft.originalImageAsset = fileAsset;
  draft.croppedImageAsset = _cloneDeep(fileAsset);
  return { src, draft };
}
